import React, { useCallback, useState } from "react";
import moment from 'moment';
import useSelector from "../../../util/hooks/useSelector";
import { RootState } from "../../../reducers";
import DatePicker from "../../../components/DatePicker";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { FastCalculationContactFormData } from "../../calculation_new/redux";
import useDispatch from "../../../util/hooks/useDispatch";
import { getShippingScheduler } from "../../creation/action_creators";
import classNames from "classnames";
import { RateCast } from "../../cast/types";

type Props = {
    currentRateCast?: RateCast | null,
    isSplitStyles?: boolean
}

const PickupDateField = ({
    currentRateCast,
    isSplitStyles
}: Props) => {

    const dispatch = useDispatch();

    const { t: _, i18n } = useTranslation('creation');

    const { currentRate, isDeferredPickup } = useSelector((state: RootState) => state.calculation);
    const { availableCreationDates } = useSelector((state) => state.creation);
    const { cargoId } = useSelector((state) => state.cargo);
    const [isFocused, setIsFocused] = useState<boolean>(false);

    const isUrgentPickupEnabled = currentRate?.deliveryService !== "pochta" && !isDeferredPickup;

    const today = React.useRef(moment()).current;

    const {
        values, setFieldValue, errors, setFieldTouched
    } = useFormikContext<FastCalculationContactFormData>();

    const handleMonthChange = useCallback(
        (val: moment.Moment) => {
            const rateCode = currentRateCast ? currentRateCast.code : currentRate?.code;
            const deliveryService = currentRateCast ? currentRateCast.deliveryService : (currentRate?.originalDeliveryService || currentRate?.deliveryService as string);
            const maxDays = currentRateCast ? currentRateCast.maxDays : (currentRate?.maxDays as number);
            if (rateCode && deliveryService) {
                const startDate = val.clone().startOf('month').startOf('week');
                const endDate = startDate.clone().add(5, 'week').endOf('week');
                const { sender } = values;
                dispatch(getShippingScheduler({
                    startDate,
                    endDate,
                    days: maxDays,
                    deliveryService,
                    lat: sender.latitude,
                    long: sender.longitude,
                    tariffList: [deliveryService === "dellin" ? `${rateCode}::${cargoId}` : rateCode],
                    deferredPickup: isDeferredPickup,
                }));
            }
        },
        [dispatch, currentRate, values, currentRateCast, cargoId, isDeferredPickup],
    );

    React.useEffect(() => {
        const rateCode = currentRateCast ? currentRateCast.code : currentRate?.code;
        const deliveryService = currentRateCast ? currentRateCast.deliveryService : (currentRate?.originalDeliveryService || currentRate?.deliveryService as string);
        const maxDays = currentRateCast ? currentRateCast.maxDays : (currentRate?.maxDays as number);
        if (rateCode && deliveryService) {
            const startDate = today.clone();
            const endDate = startDate.clone().add(1, 'month');
            const { sender } = values;
            dispatch(getShippingScheduler({
                startDate,
                endDate,
                days: maxDays,
                deliveryService,
                lat: sender.latitude,
                long: sender.longitude,
                tariffList: [deliveryService === "dellin" ? `${rateCode}::${cargoId}` : rateCode],
                deferredPickup: isDeferredPickup,
            })).then(
                ([firstAvailable]) => {
                    if (firstAvailable) setFieldValue('pickupDate', firstAvailable.date);
                },
            );
        }
    }, [isDeferredPickup]);

    return (
        <div className={classNames("gx-d-flex gx-flex-column", {"gx-mt-auto": !isSplitStyles, "gx-w-100": isSplitStyles})}>
            <div className={classNames("custom-split-calculation-picker", {"gx-mt-2": !isSplitStyles, "is-split-m-0": isSplitStyles, "is-filled": isFocused || values.pickupDate, "is-focused": isFocused, "is-error": errors.pickupDate})}>
                <label className="custom-split-calculation-picker__label">Дата сбора</label>
                {
                    (currentRate || currentRateCast)
                    ?
                    <DatePicker
                        placeholder={today.format('DD.MM.YYYY')}
                        value={values.pickupDate?.locale(i18n.language)}
                        onChange={(val: any) => setFieldValue('pickupDate', val)}
                        defaultValue={today.locale(i18n.language)}
                        disabledDate={(d: any) => {
                            if (!d) return true;
                            const isInAvailableDates = availableCreationDates.find((_) => d.isSame(_.date, 'day'));
                            if (isUrgentPickupEnabled) {
                                const isToday = d.isSame(today, 'day');
                                const now = moment();
                                const isBefore1730 = now.hour() < 17 || (now.hour() === 17 && now.minute() < 30);
                                if (isToday && isBefore1730) return false;
                            }
                            return !isInAvailableDates;
                        }}
                        showToday={false}
                        format="DD.MM.YYYY"
                        className='gx-w-100 custom-split-calculation-picker__input'
                        allowClear={false}
                        // @ts-ignore
                        onPanelValueChange={handleMonthChange}
                        onFocus={() => {
                            setIsFocused(true);
                            setFieldTouched('pickupDate', true)
                        }}
                        onBlur={() => {
                            setIsFocused(false);
                        }}
                    />
                    :
                    <DatePicker
                        placeholder={today.format('DD.MM.YYYY')}
                        value={values.pickupDate?.locale(i18n.language)}
                        onChange={(val: any) => setFieldValue('pickupDate', val)}
                        disabledDate={(d: any) => {
                            if (!d) return true;
                            const todayStart = moment().startOf('day');
                            const isToday = d.isSame(todayStart, 'day');
                            const isPast = d.isBefore(todayStart, 'day');
                            if (isPast) return true;
                            if (isUrgentPickupEnabled) {
                                const now = moment();
                                const isBefore1730 = now.hour() < 17 || (now.hour() === 17 && now.minute() < 30);
                                if (isToday && isBefore1730) return false;
                            }
                            const nowUtc = moment.utc();
                            if (isToday && nowUtc.hour() >= 12) return true;
                            return false;
                        }}
                        defaultValue={today.locale(i18n.language)}
                        showToday={false}
                        format="DD.MM.YYYY"
                        className='gx-w-100 custom-split-calculation-picker__input'
                        allowClear={false}
                        onFocus={() => {
                            setIsFocused(true);
                            setFieldTouched('pickupDate', true)
                        }}
                        onBlur={() => {
                            setIsFocused(false);
                        }}
                    />
                }
            </div>
        </div>
    )
}

export { PickupDateField }
